import {
  createContext,
  type Dispatch,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import {
  useFilterExpressions,
  withMultiEntityFilters,
} from '../../filters/contexts/FilterExpressions';
import { ALL_FILTER_DEFINITIONS } from '../../filters/engine/filterRegistry';
import { FilterId, FilterType } from '../../filters/types/filters';
import { NavigationGuard } from '../../hooks/NavigationGuard';
import { PageExportProvider } from '../Chart/chart-menu/PageExportProvider';
import { useEditMode } from '../EditModeProvider/EditModeProvider';
import { usePageManager } from '../PageManagerProvider/PageManagerProvider';
import { usePageState } from '../PageState/PageStateProvider';
import {
  type PageAction,
  pageReducer,
  type ReportParameterConfig,
  type TabContent,
  type TabContentAction,
  type TabContentView,
} from './page-reducer';
import type { Page, Tab } from './types';
import type { PositionedItem } from './types/item';
import { useReducerWithHistory } from './useReducerWithHistory';

export type PageContextType = {
  config: Page;
  dispatch: Dispatch<PageAction>;
  saveChanges: () => Promise<boolean>;
  discardChanges: () => void;
  hasUnsavedChanges: boolean;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
};

const PageContext = createContext<PageContextType | undefined>(undefined);

/** PageContext plus guard/export wrappers around an externally owned value (e.g. ReportDocumentProvider). */
export const PageConfigProviderFromValue = ({
  value,
  children,
}: {
  value: PageContextType;
  children: ReactNode;
}) => {
  const { editMode } = useEditMode();

  return (
    <PageContext.Provider value={value}>
      <NavigationGuard
        hasUnsavedChanges={editMode && value.hasUnsavedChanges}
        onDiscardChanges={value.discardChanges}
      />
      <PageExportProvider>{children}</PageExportProvider>
    </PageContext.Provider>
  );
};

type ChildrenProps = {
  tabContent: TabContent;
};

type PageProviderProps = {
  initialConfig: Page;
  /**
   * True when `initialConfig` is a freshly-generated draft that has never
   * been persisted (e.g. an AI-built dashboard). Its content IS the history
   * baseline, so `historyIndex === lastSavedIndex` and the plain
   * unsaved-changes check reads false — leaving a never-saved page with real
   * content unprotected by the navigation guards. Treated as unsaved (once it
   * has content, until the first save) so leaving it prompts.
   */
  isUnsavedDraft?: boolean;
  children: (props: ChildrenProps) => ReactNode;
};

export function PageProvider({
  children,
  initialConfig,
  isUnsavedDraft = false,
}: PageProviderProps) {
  const [originalConfig, setOriginalConfig] = useState<Page>(initialConfig);
  // Latches true on the first successful save so a saved-then-edited draft
  // falls back to the pure history check (and never re-prompts post-save,
  // which a static `isUnsavedDraft` prop alone would).
  const [hasBeenSaved, setHasBeenSaved] = useState(false);

  const {
    state: draftConfig,
    dispatch: dispatchDraft,
    undo: internalUndo,
    redo: internalRedo,
    canUndo,
    canRedo,
    historyIndex,
  } = useReducerWithHistory<Page, PageAction>(pageReducer, initialConfig);

  const [lastSavedIndex, setLastSavedIndex] = useState<number>(historyIndex);

  const undo = useCallback(() => {
    internalUndo();
  }, [internalUndo]);

  const redo = useCallback(() => {
    internalRedo();
  }, [internalRedo]);

  const { editMode } = useEditMode();
  const { createOrUpdatePage } = usePageManager();

  //allows us to dispatch actions to the draft config in edit mode, and to the original config in view mode
  const dispatch: Dispatch<PageAction> = useCallback(
    (action) => {
      if (editMode) {
        dispatchDraft(action);
      } else {
        setOriginalConfig((prev) => pageReducer(prev, action));
      }
    },
    [editMode, dispatchDraft]
  );

  const config = editMode ? draftConfig : originalConfig;

  const pageHasContent =
    config?.tabs?.some((tab) => tab.tabContent.items.length > 0) ?? false;
  const hasUnsavedChanges =
    historyIndex !== lastSavedIndex ||
    (isUnsavedDraft && !hasBeenSaved && pageHasContent);

  const discardChanges = useCallback(() => {
    dispatchDraft({ type: 'SET_CONFIG', payload: originalConfig });
  }, [dispatchDraft, originalConfig]);

  const saveChanges = async (): Promise<boolean> => {
    if (!draftConfig) return false;
    const saved = await createOrUpdatePage(draftConfig);
    if (saved) {
      setOriginalConfig(saved);
      setLastSavedIndex(historyIndex);
      setHasBeenSaved(true);
      // Return a promise that resolves after state updates
      return new Promise<boolean>((resolve) => {
        // Wait for the next render cycle to have hasUnsavedChanges updated
        setTimeout(() => resolve(true), 0);
      });
    }

    return false;
  };

  return (
    <PageContext.Provider
      value={{
        config,
        dispatch,
        saveChanges,
        discardChanges,
        hasUnsavedChanges,
        undo,
        redo,
        canUndo,
        canRedo,
      }}
    >
      {/* Rendered, not called as a hook — see NavigationGuard: the router
          blocker must only exist while there are unsaved changes. */}
      <NavigationGuard
        hasUnsavedChanges={editMode && hasUnsavedChanges}
        onDiscardChanges={discardChanges}
      />
      <PageExportProvider>
        <TabContentProvider>{children}</TabContentProvider>
      </PageExportProvider>
    </PageContext.Provider>
  );
}

export const usePageConfig = () => {
  const context = useContext(PageContext);
  if (context === undefined) {
    throw new Error('usePageConfig must be used within a PageProvider');
  }
  return {
    config: context.config,
    dispatch: context.dispatch,
    saveChanges: context.saveChanges,
    discardChanges: context.discardChanges,
    hasUnsavedChanges: context.hasUnsavedChanges,
    undo: context.undo,
    redo: context.redo,
    canUndo: context.canUndo,
    canRedo: context.canRedo,
  };
};

/**
 * The page id when rendered under a `PageProvider`, else undefined. For
 * analytics props on components that also render outside a page (stories, the
 * report builder), where `useTabContentConfig` would throw.
 */
export const useOptionalRevelioPageId = () =>
  useContext(PageContext)?.config?.revelioPageId;

type TabContentContextValue = {
  config: TabContentView;
  items: PositionedItem[];
  dispatch: Dispatch<TabContentAction>;
};

const TabContentContext = createContext<TabContentContextValue | undefined>(
  undefined
);

export const useOptionalTabContentItems = () =>
  useContext(TabContentContext)?.items;

export const useOptionalTabContentConfig = () =>
  useContext(TabContentContext)?.config;

const NO_REPORT_PARAMETERS: ReportParameterConfig[] = [];

/** Parameters of the tab being rendered, else of the page's first tab (report pages have one). */
export const useReportParameters = () => {
  const tabContent = useContext(TabContentContext)?.config;
  const page = useContext(PageContext)?.config;
  return (
    tabContent?.reportParameters ??
    page?.tabs?.[0]?.tabContent?.reportParameters ??
    NO_REPORT_PARAMETERS
  );
};

/** TabContentContext around a value another owner computed (the report document provider). */
export const TabContentProviderFromValue = ({
  value,
  children,
}: {
  value: TabContentContextValue;
  children: ReactNode;
}) => (
  <TabContentContext.Provider value={value}>
    {children}
  </TabContentContext.Provider>
);

/**
 * Provides `TabContentContext` for a specific tab, with a dispatch scoped to
 * that tab. `KeepAliveTabGrids` renders one per visited tab so each kept-alive
 * grid reads its own tab's content instead of only the active tab's.
 */
export const TabContentProviderForTab = ({
  tab,
  children,
}: {
  tab: Tab;
  children: ReactNode;
}) => {
  const pageContext = useContext(PageContext);
  if (!pageContext) {
    throw new Error(
      'TabContentProviderForTab must be used within a PageProvider'
    );
  }

  const tabId = tab.id;
  const pageDispatch = pageContext.dispatch;
  const dispatch = useCallback(
    (action: TabContentAction) => {
      pageDispatch({
        type: 'APPLY_TAB_CONTENT_ACTION',
        payload: { tabId, action },
        ...(action.replacePreviousHistorySnapshot
          ? { replacePreviousHistorySnapshot: true }
          : {}),
      });
    },
    [pageDispatch, tabId]
  );

  return (
    <TabContentContext.Provider
      value={{ config: tab.tabContent, items: tab.tabContent.items, dispatch }}
    >
      {children}
    </TabContentContext.Provider>
  );
};

const TabContentProvider = ({
  children,
}: {
  children: (props: { tabContent: TabContent }) => ReactNode;
}) => {
  const pageContext = useContext(PageContext);
  if (!pageContext) {
    throw new Error('TabContentProvider must be used within a PageProvider');
  }

  const { pageState } = usePageState();
  const activeTabId = pageState.activeTabId;

  const activeTab =
    pageContext.config.tabs.find((t) => t.id === activeTabId) ??
    pageContext.config.tabs.find((t) => t.isActive) ??
    pageContext.config.tabs[0];

  if (!activeTab) return null;

  return (
    <TabContentProviderForTab tab={activeTab}>
      {children({ tabContent: activeTab.tabContent })}
    </TabContentProviderForTab>
  );
};

export function useTabContentConfig() {
  const tabContentContext = useContext(TabContentContext);
  const parentPageContext = useContext(PageContext);
  const usesFilterExpressions = useFilterExpressions();
  const revelioPageId = parentPageContext?.config?.revelioPageId;

  if (tabContentContext === undefined) {
    throw new Error(
      'useTabContentConfig must be used within a TabContentProvider'
    );
  }

  const primaryFilter = tabContentContext.config.primaryFilter;

  const hasPrimaryFilter = (filterId: FilterId) =>
    primaryFilter ? primaryFilter.id === filterId : false;

  const secondaryFilters = usesFilterExpressions
    ? withMultiEntityFilters(
        tabContentContext.config.secondaryFilters,
        primaryFilter?.id
      )
    : tabContentContext.config.secondaryFilters;
  const hasSecondaryFilter = (filterId: FilterId) => {
    if (secondaryFilters.length === 0) {
      return true;
    }

    // Flatten secondaryFilters: expand GROUP filters to their children,
    // and MULTI segment filters to include their wrapped id so single-segment
    // AI filters (e.g. HEADCOUNT) are considered on-page when MULTI_HEADCOUNT is in the template.
    const flattenedSecondaryFilterIds = secondaryFilters.flatMap(
      (secondaryFilter) => {
        const filterDef = ALL_FILTER_DEFINITIONS[secondaryFilter.id];
        if (filterDef?.type === FilterType.GROUP && filterDef.children) {
          return filterDef.children;
        }
        if (
          filterDef?.type === FilterType.MULTI &&
          'wrappedFilterId' in filterDef &&
          filterDef.wrappedFilterId
        ) {
          return [secondaryFilter.id, filterDef.wrappedFilterId];
        }
        return [secondaryFilter.id];
      }
    );

    return flattenedSecondaryFilterIds.includes(filterId);
  };

  const hasFilter = (filterId: FilterId) =>
    hasPrimaryFilter(filterId) || hasSecondaryFilter(filterId);

  const dateFilterId: FilterId.DATE_RANGE | FilterId.SNAPSHOT_DATE =
    hasSecondaryFilter(FilterId.DATE_RANGE)
      ? FilterId.DATE_RANGE
      : FilterId.SNAPSHOT_DATE;

  // Remove Revelio-only unofficial template options
  const rawConfig = tabContentContext?.config;
  let config = usesFilterExpressions
    ? { ...rawConfig, secondaryFilters }
    : rawConfig;
  if (rawConfig && !revelioPageId) {
    config = { ...config, dateRangeDefault: undefined };
  }

  return {
    config,
    items: tabContentContext.items,
    primaryFilter,
    parentId: parentPageContext?.config?.id,
    revelioPageId,
    dispatch: tabContentContext?.dispatch,
    hasFilter,
    hasSecondaryFilter,
    dateFilterId,
  };
}
